import React, { useState, useEffect } from "react";
import {
  PrinterIcon,
  QrCodeIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import { request } from "config/request";
import QRCode from "react-qr-code";
import Select from "react-select";
import { ListCommanWay } from "API/commonApi";
import { useQuery } from "@tanstack/react-query";
import UseToast from "utils/AfToast";
import { soundFX } from "../soundEffects";
import { useAuthStore } from "store/auth";

const compactSelectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    background: "white",
    borderRadius: "10px",
    borderColor: state.isFocused ? "#0077B6" : "#cbd5e1",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(0, 119, 182, 0.15)" : "none",
    minHeight: "36px",
    fontSize: "12px",
  }),
  valueContainer: (base: any) => ({ ...base, padding: "0 8px" }),
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
};

export const StickerPrinterTab: React.FC = () => {
  const user = useAuthStore((s) => s.user);

  const [lastSticker, setLastSticker] = useState<any>(null);
  const [loadingLast, setLoadingLast] = useState(false);

  const [prefix, setPrefix] = useState("BX-");
  const [startNo, setStartNo] = useState<number>(1);
  const [qty, setQty] = useState<number>(50);
  const [notes, setNotes] = useState("");
  const [stickerLayout, setStickerLayout] = useState<"sheet" | "thermal">("sheet");

  const [preRegisterStock, setPreRegisterStock] = useState(false);
  const [selectedSubbranch, setSelectedSubbranch] = useState<any>(null);
  const [boxType, setBoxType] = useState<"SINGLE_USE" | "MULTI_USE">("SINGLE_USE");
  const [savingRecord, setSavingRecord] = useState(false);

  const { data: subbranchesData } = useQuery(["SubBranchesForStickers"], () =>
    ListCommanWay("/admin/subbranch/list", {}, { paginate: 1000 })
  );

  const subbranchOptions = (subbranchesData?.data?.data || []).map((sb: any) => ({
    value: sb.id,
    label: `${sb.branch_name || sb.name || "Hub"} ${sb.code ? `(${sb.code})` : ""}`,
  }));

  const fetchLastSticker = async () => {
    setLoadingLast(true);
    try {
      const res: any = await request({ url: "/admin/box-sticker/last", method: "GET" });
      if (res?.status === "SUCCESS" && res.data) {
        setLastSticker(res.data);
        const lastNo = res.data.lastPrintedNo || 0;
        setStartNo(lastNo + 1);
        if (res.data.prefix) setPrefix(res.data.prefix);
      }
    } catch {} finally {
      setLoadingLast(false);
    }
  };

  useEffect(() => {
    fetchLastSticker();
  }, []);

  useEffect(() => {
    if (subbranchOptions.length > 0 && !selectedSubbranch) {
      const userSubbranch = (user as any)?.subbranch;
      if (userSubbranch) {
        const found = subbranchOptions.find((o: any) => o.value === userSubbranch);
        if (found) setSelectedSubbranch(found);
      } else {
        setSelectedSubbranch(subbranchOptions[0]);
      }
    }
  }, [subbranchOptions, user]);

  const endNo = startNo + Math.max(1, qty) - 1;

  const formatCode = (num: number) => `${prefix}${String(num).padStart(5, "0")}`;
  const startCode = formatCode(startNo);
  const endCode = formatCode(endNo);

  const generatedCodes: string[] = [];
  const previewLimit = Math.min(qty, 100);
  for (let i = startNo; i <= startNo + previewLimit - 1; i++) {
    generatedCodes.push(formatCode(i));
  }

  const handlePrint = async () => {
    if (qty <= 0) return;
    try {
      setSavingRecord(true);

      await request({
        url: "/admin/box-sticker/record",
        method: "POST",
        body: {
          prefix,
          lastPrintedNo: endNo,
          lastPrintedCode: endCode,
          qty,
          notes: notes.trim() || `Printed ${qty} stickers (${startCode} to ${endCode})`,
        },
      });

      if (preRegisterStock) {
        await request({
          url: "/admin/box-tracker/register-batch",
          method: "POST",
          body: {
            prefix,
            startNo,
            endNo,
            subbranch: selectedSubbranch?.value || undefined,
            boxType,
          },
        });
        UseToast("Batch registered into stock and sequence saved!", "success");
      } else {
        UseToast("Print sequence recorded successfully!", "success");
      }

      soundFX.playSuccess();
      fetchLastSticker();

      setTimeout(() => {
        window.print();
      }, 300);
    } catch (err: any) {
      soundFX.playError();
      UseToast(typeof err === "string" ? err : err?.message || "Error saving sticker record.", "error");
    } finally {
      setSavingRecord(false);
    }
  };

  return (
    <div className="space-y-6">
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #box-printable-area, #box-printable-area * { visibility: visible !important; }
          #box-printable-area { position: absolute !important; left: 0 !important; top: 0 !important; width: 100% !important; margin: 0 !important; padding: 10px !important; }
          .no-print { display: none !important; }
        }
      `}</style>

      {/* Clean Status Header */}
      <div className="no-print bg-white rounded-xl border border-slate-200 p-3.5 flex items-center justify-between shadow-xs">
        <div className="flex items-center gap-2">
          <PrinterIcon className="w-5 h-5 text-[#0077B6]" />
          <span className="text-xs font-bold text-slate-800">Print Box Stickers</span>
        </div>

        <div className="flex items-center gap-2 text-xs">
          <span className="text-slate-400">Last Printed:</span>
          <span className="font-mono font-bold text-slate-800">
            {loadingLast ? "..." : lastSticker?.lastPrintedCode || "BX-00000"}
          </span>
          <button type="button" onClick={fetchLastSticker} className="p-1 rounded text-slate-400 hover:text-slate-600">
            <ArrowPathIcon className={`w-3.5 h-3.5 ${loadingLast ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      <div className="no-print grid grid-cols-12 md:grid-cols-1 gap-6">
        {/* Parameters */}
        <div className="col-span-5 md:col-span-1 space-y-4">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-xs p-5 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-semibold text-slate-700">Prefix</label>
                  <div className="flex gap-1">
                    {["BX-", "AHD", ""].map((p) => (
                      <button
                        key={p || "num"}
                        type="button"
                        onClick={() => setPrefix(p)}
                        className={`text-[10px] px-1.5 py-0.5 rounded font-mono font-bold border transition-colors ${
                          prefix === p
                            ? "bg-[#0077B6] text-white border-[#0077B6]"
                            : "bg-slate-100 text-slate-600 hover:bg-slate-200 border-slate-200"
                        }`}
                      >
                        {p || "Numeric"}
                      </button>
                    ))}
                  </div>
                </div>
                <input
                  type="text"
                  value={prefix}
                  onChange={(e) => setPrefix(e.target.value.toUpperCase())}
                  placeholder="e.g. BX-, AHD, or empty for numeric"
                  className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg font-mono font-bold"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Start No</label>
                <input
                  type="number"
                  min={1}
                  value={startNo}
                  onChange={(e) => setStartNo(Math.max(1, Number(e.target.value)))}
                  className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg font-mono font-bold"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Quantity</label>
              <div className="flex gap-2 mb-2">
                {[20, 50, 100].map((q) => (
                  <button
                    key={q}
                    type="button"
                    onClick={() => setQty(q)}
                    className={`flex-1 py-1.5 text-xs font-semibold rounded-lg border ${
                      qty === q ? "bg-purple-600 text-white border-purple-600" : "bg-slate-50 text-slate-700"
                    }`}
                  >
                    {q}
                  </button>
                ))}
              </div>
              <input
                type="number"
                min={1}
                value={qty}
                onChange={(e) => setQty(Math.max(1, Number(e.target.value)))}
                className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg font-mono"
              />
            </div>

            <div className="p-3 bg-purple-50 rounded-xl border border-purple-200 flex items-center justify-between">
              <div>
                <span className="text-[10px] font-bold uppercase text-purple-700 block">Print Range</span>
                <span className="font-mono font-bold text-purple-950 text-sm">{startCode} &rarr; {endCode}</span>
              </div>
              <span className="text-xs bg-purple-200 text-purple-900 font-bold px-2 py-0.5 rounded-full">{qty} Units</span>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Layout</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setStickerLayout("sheet")}
                  className={`py-2 text-xs font-semibold rounded-lg border ${stickerLayout === "sheet" ? "bg-purple-600 text-white" : "bg-slate-50 text-slate-700"}`}
                >
                  A4 Sheet Grid
                </button>
                <button
                  type="button"
                  onClick={() => setStickerLayout("thermal")}
                  className={`py-2 text-xs font-semibold rounded-lg border ${stickerLayout === "thermal" ? "bg-purple-600 text-white" : "bg-slate-50 text-slate-700"}`}
                >
                  Thermal Roll
                </button>
              </div>
            </div>

            <div className="pt-2 border-t border-slate-100 space-y-3">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={preRegisterStock}
                  onChange={(e) => setPreRegisterStock(e.target.checked)}
                  className="w-4 h-4 text-purple-600 rounded"
                />
                <span className="text-xs font-bold text-slate-800">Pre-register in Stock immediately</span>
              </label>

              {preRegisterStock && (
                <div className="p-3 bg-slate-50 border rounded-xl space-y-3">
                  <Select options={subbranchOptions} value={selectedSubbranch} onChange={setSelectedSubbranch} styles={compactSelectStyles} placeholder="Assign Hub..." />
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setBoxType("SINGLE_USE")}
                      className={`py-1.5 text-xs font-semibold rounded-lg border ${boxType === "SINGLE_USE" ? "bg-slate-800 text-white" : "bg-white text-slate-700"}`}
                    >
                      Single-Use
                    </button>
                    <button
                      type="button"
                      onClick={() => setBoxType("MULTI_USE")}
                      className={`py-1.5 text-xs font-semibold rounded-lg border ${boxType === "MULTI_USE" ? "bg-teal-700 text-white" : "bg-white text-slate-700"}`}
                    >
                      Multi-Use
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Notes</label>
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g. Roll #12 for Surat station..."
                className="w-full px-3 py-2 text-xs border rounded-lg"
              />
            </div>

            <button
              type="button"
              disabled={savingRecord || qty <= 0}
              onClick={handlePrint}
              className="w-full py-3.5 px-4 bg-gradient-to-r from-purple-700 to-indigo-700 hover:from-purple-800 hover:to-indigo-800 text-white rounded-xl font-bold text-xs shadow-md transition-all flex items-center justify-center gap-2 disabled:opacity-40"
            >
              {savingRecord ? "Saving & Printing..." : `Print & Record Batch (${qty} Stickers)`}
            </button>
          </div>
        </div>

        {/* Live Preview */}
        <div className="col-span-7 md:col-span-1 space-y-4">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-xs p-5 space-y-3">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5 pb-2 border-b">
              <PrinterIcon className="w-4 h-4 text-purple-600" />
              <span>Live Sticker Preview ({generatedCodes.length} shown)</span>
            </h3>

            <div className="border border-slate-200 rounded-xl p-4 bg-slate-50 max-h-[580px] overflow-y-auto">
              <div className={stickerLayout === "sheet" ? "grid grid-cols-3 sm:grid-cols-2 gap-3" : "flex flex-col items-center space-y-4"}>
                {generatedCodes.map((code) => (
                  <div key={code} className="bg-white border-2 border-slate-800 rounded-xl p-3 flex flex-col items-center justify-center text-center w-full max-w-[200px]">
                    <div className="w-full flex items-center justify-between border-b pb-1 mb-2">
                      <span className="text-[9px] font-black tracking-wider text-slate-900 uppercase">AIRFIELD</span>
                      <span className="text-[8px] font-mono font-bold text-slate-500">{boxType === "MULTI_USE" ? "REUSABLE" : "CARTON"}</span>
                    </div>
                    <div className="p-1.5 bg-white rounded border flex items-center justify-center">
                      <QRCode value={code} size={84} level="M" />
                    </div>
                    <span className="font-mono font-black text-slate-900 text-sm mt-1.5">{code}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Hidden printable area */}
      <div id="box-printable-area" className="hidden print:block">
        <div className={stickerLayout === "sheet" ? "grid grid-cols-3 gap-4 p-4" : "flex flex-col items-center space-y-6 p-4"}>
          {generatedCodes.map((code) => (
            <div key={code} className="border-2 border-black rounded-lg p-3 flex flex-col items-center justify-center text-center w-[180px] h-[180px]">
              <div className="w-full flex items-center justify-between border-b border-black pb-1 mb-1.5">
                <span className="text-[9px] font-black uppercase">AIRFIELD CARGO</span>
                <span className="text-[8px] font-mono font-bold">{boxType === "MULTI_USE" ? "MULTI-USE" : "CARTON"}</span>
              </div>
              <div className="p-1 bg-white flex items-center justify-center">
                <QRCode value={code} size={90} level="M" />
              </div>
              <span className="font-mono font-black text-black text-sm mt-1">{code}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
